/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { IQueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { ProductList } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import {
    OrderTemplate,
    getOrderTemplates,
    GetOrderTemplatesInput,
    QueryResultSettingsProxy
} from '@msdyn365-commerce-modules/retail-actions';
import { getProductListCountsAsync } from './get-product-list-counts';

/**
 *  Input class for the GetOrderTemplates data action.
 */
export class GetFullOrderTemplatesInput implements IActionInput {
    public queryResultSettings: IQueryResultSettings;
    public getPrice?: boolean;
    public getRating?: boolean;

    constructor(queryResultSettings: IQueryResultSettings, getPrices = true, getRatings = true) {
        this.queryResultSettings = queryResultSettings;
        this.getPrice = getPrices;
        this.getRating = getRatings;
    }

    public getCacheKey = () => 'GetFullOrderTemplates';

    public getCacheObjectType = () => 'GetFullOrderTemplates';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the GetOrderTemplate method.
 * @param inputData The input data passed to the createInput method.
 */
export const createGetFullOrderTemplatesInput = (inputData: ICreateActionContext): GetFullOrderTemplatesInput => {
    const { requestContext } = inputData;
    if (!requestContext.user.isAuthenticated) {
        throw new Error('Unable to get full order templates. User is not authenticated.');
    }

    const queryResultSettings: IQueryResultSettings = QueryResultSettingsProxy.fromInputData(inputData).QueryResultSettings;
    queryResultSettings.count = true;

    return new GetFullOrderTemplatesInput(queryResultSettings);
};

/**
 * The action method for the GetFullOrderTemplates data action.
 * @param input The action input.
 * @param ctx The action context.
 */
export async function getFullOrderTemplatesAction(input: GetFullOrderTemplatesInput, ctx: IActionContext): Promise<OrderTemplate[]> {
    if (!ctx.requestContext.user.isAuthenticated) {
        throw new Error('Unable to get full order templates. User is not authenticated.');
    }

    const orderTemplates: OrderTemplate[] = [];

    const inputParams = new GetOrderTemplatesInput();
    const productLists: ProductList[] = await getOrderTemplates(inputParams, ctx);

    if (!productLists || productLists.length === 0) {
        return orderTemplates;
    }

    const counts = await getProductListCountsAsync(ctx, productLists.map(x => x.Id));

    productLists.forEach(x => orderTemplates.push(new OrderTemplate(x, [], counts.find(count => count.ProductListId === x.Id)?.RowCount ?? 0)));

    return orderTemplates;
}

/**
 * The GetFullOrderTemplates Data Action
 * Returns order templates.
 */
export const getFullOrderTemplatesActionDataAction = createObservableDataAction<OrderTemplate[]>({
    id: '@msdyn365-commerce-modules/retail-actions/order-templates/get-full-order-templates',
    action: <IAction<OrderTemplate[]>>getFullOrderTemplatesAction,
    input: createGetFullOrderTemplatesInput
});

export default getFullOrderTemplatesActionDataAction;
